// src/pages/DashboardPage.jsx
import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { useApiClient } from "../apiClient";

function formatInstant(iso) {
  if (!iso) return "—";
  try {
    return new Date(iso).toLocaleString(undefined, {
      dateStyle: "medium",
      timeStyle: "short",
    });
  } catch {
    return iso;
  }
}

export default function DashboardPage() {
  const { authorizedFetch } = useApiClient();
  const [stats, setStats] = useState(null);
  const [status, setStatus] = useState({ loading: true, error: "" });

  useEffect(() => {
    let cancelled = false;

    async function load() {
      setStatus({ loading: true, error: "" });
      try {
        const data = await authorizedFetch("/dashboard");
        if (!cancelled) {
          setStats(data);
          setStatus({ loading: false, error: "" });
        }
      } catch (err) {
        if (!cancelled) {
          setStatus({
            loading: false,
            error: err.message || "Failed to load dashboard",
          });
        }
      }
    }

    load();
    return () => {
      cancelled = true;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  if (status.loading) {
    return (
      <section className="page page-dashboard">
        <h1>Dashboard</h1>
        <p className="page-subtitle">Loading statistics…</p>
      </section>
    );
  }

  if (status.error) {
    return (
      <section className="page page-dashboard">
        <h1>Dashboard</h1>
        <div className="alert alert-error">{status.error}</div>
      </section>
    );
  }

  return (
    <section className="page page-dashboard">
      <h1>Dashboard</h1>
      <p className="page-subtitle">Library overview and activity</p>

      <div className="dashboard-grid">
        <div className="stat-card">
          <div className="stat-label">Unique books</div>
          <div className="stat-value">{stats.uniqueBookCount}</div>
          <p className="stat-hint">
            Same work in multiple formats counts once when ISBN is known;
            otherwise each upload is counted separately.
          </p>
        </div>
        <div className="stat-card">
          <div className="stat-label">Total downloads</div>
          <div className="stat-value">{stats.totalDownloads}</div>
        </div>
      </div>

      <h2 className="dashboard-section-title">Files by format</h2>
      <div className="format-badges">
        <span className="format-badge">
          PDF <strong>{stats.pdfFileCount}</strong>
        </span>
        <span className="format-badge">
          EPUB <strong>{stats.epubFileCount}</strong>
        </span>
        <span className="format-badge">
          MOBI <strong>{stats.mobiFileCount}</strong>
        </span>
      </div>

      <h2 className="dashboard-section-title">Recently uploaded</h2>
      {stats.recentUploads?.length === 0 ? (
        <p className="muted">No books yet.</p>
      ) : (
        <div className="table-wrap">
          <table className="data-table">
            <thead>
              <tr>
                <th>Title</th>
                <th>Format</th>
                <th>Uploaded</th>
                <th />
              </tr>
            </thead>
            <tbody>
              {stats.recentUploads.map((row) => (
                <tr key={row.bookId}>
                  <td>{row.title}</td>
                  <td>{row.format}</td>
                  <td>{formatInstant(row.uploadedAt)}</td>
                  <td className="cell-actions">
                    <Link to={`/books/${row.bookId}`} className="table-link">
                      View
                    </Link>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      <h2 className="dashboard-section-title">Most downloaded</h2>
      {stats.topDownloadedBooks?.length === 0 ? (
        <p className="muted">No downloads recorded yet.</p>
      ) : (
        <div className="table-wrap">
          <table className="data-table">
            <thead>
              <tr>
                <th>Title</th>
                <th>Format</th>
                <th>Downloads</th>
                <th />
              </tr>
            </thead>
            <tbody>
              {stats.topDownloadedBooks.map((row) => (
                <tr key={row.bookId}>
                  <td>{row.title}</td>
                  <td>{row.format}</td>
                  <td>{row.downloadCount}</td>
                  <td className="cell-actions">
                    <Link to={`/books/${row.bookId}`} className="table-link">
                      View
                    </Link>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </section>
  );
}
